/**
 * Global date error handler
 * This patches Date constructor and common array methods to catch date errors
 */

// Store original methods
const OriginalDate = window.Date;
const originalFilter = Array.prototype.filter;
const originalMap = Array.prototype.map;
const originalSort = Array.prototype.sort;

// Error tracking
export const dateErrors: any[] = [];

/**
 * Initialize global date error handling
 */
export function initializeDateErrorHandling() {
  // Patch Date constructor
  (window as any).Date = function(...args: any[]) {
    try {
      const date = new (OriginalDate as any)(...args);
      
      if (isNaN(date.getTime()) && args.length > 0 && args[0] !== undefined) {
        console.warn('[Date Error] Invalid date created:', args);
        dateErrors.push({
          type: 'constructor',
          args,
          stack: new Error().stack,
          timestamp: new Date().toISOString()
        });
      }
      
      return date;
    } catch (error) {
      console.error('[Date Error] Date constructor threw:', error);
      dateErrors.push({
        type: 'constructor_error',
        args,
        error,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  };

  // Copy Date static methods
  Object.setPrototypeOf(window.Date, OriginalDate);
  Object.setPrototypeOf(window.Date.prototype, OriginalDate.prototype);
  
  // Copy static properties
  const descriptors = Object.getOwnPropertyDescriptors(OriginalDate);
  Object.keys(descriptors).forEach(key => {
    if (key !== 'prototype' && key !== 'length' && key !== 'name') {
      try {
        Object.defineProperty(window.Date, key, descriptors[key]);
      } catch {
        // Some properties might be non-configurable
      }
    }
  });

  // Patch Array.prototype.filter
  Array.prototype.filter = function(callback: any, thisArg?: any) {
    try {
      return originalFilter.call(this, callback, thisArg);
    } catch (error: any) {
      if (error.message === 'Invalid time value') {
        console.error('[Date Error] Invalid date in filter operation');
        console.error('Array:', this);
        console.error('Callback:', callback.toString());
        
        // Try to find the problematic item
        const problematicItems: any[] = [];
        this.forEach((item, index) => {
          try {
            callback.call(thisArg, item, index, this);
          } catch (itemError: any) {
            if (itemError.message === 'Invalid time value') {
              problematicItems.push({ index, item });
            }
          }
        });
        
        if (problematicItems.length > 0) {
          console.error('Problematic items:', problematicItems);
          dateErrors.push({
            type: 'filter_error',
            problematicItems,
            callback: callback.toString(),
            timestamp: new Date().toISOString()
          });
        }
      }
      throw error;
    }
  };

  // Patch Array.prototype.map
  Array.prototype.map = function(callback: any, thisArg?: any) {
    try {
      return originalMap.call(this, callback, thisArg);
    } catch (error: any) {
      if (error.message === 'Invalid time value') {
        console.error('[Date Error] Invalid date in map operation');
        console.error('Array:', this);
        console.error('Callback:', callback.toString());
        
        dateErrors.push({
          type: 'map_error',
          array: this,
          callback: callback.toString(),
          timestamp: new Date().toISOString()
        });
      }
      throw error;
    }
  };

  // Patch Array.prototype.sort
  Array.prototype.sort = function(compareFunction?: any) {
    try {
      return originalSort.call(this, compareFunction);
    } catch (error: any) {
      if (error.message === 'Invalid time value') {
        console.error('[Date Error] Invalid date in sort operation');
        console.error('Array:', this);
        if (compareFunction) {
          console.error('Compare function:', compareFunction.toString());
        }
        
        dateErrors.push({
          type: 'sort_error',
          array: this,
          compareFunction: compareFunction?.toString(),
          timestamp: new Date().toISOString()
        });
      }
      throw error;
    }
  };

  console.log('[Date Error Handler] Initialized. Errors will be logged to:', dateErrors);
}